import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Building2, Pencil, Trash2 } from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RoleGate } from "@/components/auth/RoleGate";
import { customersService } from "@/services/customers.service";
import { getErrorMessage } from "@/services/api";
import { formatCNPJ, formatDate, formatPhone } from "@/lib/utils";
import { usePermission } from "@/hooks/usePermission";

export default function CustomersListPage() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { can } = usePermission();

    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState({ sortBy: "created_at", sortOrder: "desc" });
    const [confirm, setConfirm] = useState(null);

    const query = useQuery({
        queryKey: ["customers", { page, search, ...sort }],
        queryFn: () =>
            customersService.list({
                page,
                pageSize: 20,
                search,
                sortBy: sort.sortBy,
                sortOrder: sort.sortOrder,
            }),
    });

    const del = useMutation({
        mutationFn: (id) => customersService.remove(id),
        onSuccess: () => {
            toast.success("Cliente removido.");
            qc.invalidateQueries({ queryKey: ["customers"] });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Não foi possível remover cliente.")),
    });

    const columns = [
        {
            key: "legal_name",
            header: "Cliente",
            sortable: true,
            cell: (r) => (
                <div className="flex items-center gap-3">
                    <div className="grid h-8 w-8 place-items-center rounded-md bg-muted text-muted-foreground">
                        <Building2 className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{r.legal_name}</div>
                        <div className="truncate text-xs text-muted-foreground">{r.trade_name || "—"}</div>
                    </div>
                </div>
            ),
        },
        { key: "tax_id", header: "CNPJ/CPF", cell: (r) => formatCNPJ(r.tax_id) || "—" },
        {
            key: "city",
            header: "Cidade",
            sortable: true,
            cell: (r) => [r.city, r.state].filter(Boolean).join("/") || "—",
        },
        { key: "main_contact", header: "Contato", cell: (r) => r.main_contact || "—" },
        { key: "phone", header: "Telefone", cell: (r) => formatPhone(r.phone) || "—" },
        {
            key: "business_segment",
            header: "Segmento",
            cell: (r) =>
                r.business_segment ? (
                    <Badge variant="outline" className="text-[10px]">{r.business_segment}</Badge>
                ) : (
                    "—"
                ),
        },
        {
            key: "created_at",
            header: "Cadastro",
            sortable: true,
            cell: (r) => <span className="text-xs text-muted-foreground">{formatDate(r.created_at)}</span>,
        },
        {
            key: "actions",
            header: "",
            className: "w-24",
            cell: (r) => (
                <div className="flex justify-end gap-1">
                    <RoleGate resource="customers" action="update">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customers/${r.id}`);
                            }}
                            aria-label="Editar"
                            data-testid={`customers-edit-${r.id}`}
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                    </RoleGate>
                    <RoleGate resource="customers" action="delete">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-danger hover:text-danger"
                            onClick={(e) => {
                                e.stopPropagation();
                                setConfirm(r);
                            }}
                            aria-label="Excluir"
                            data-testid={`customers-delete-${r.id}`}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </RoleGate>
                </div>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            <PageHeader
                title="Clientes"
                description="Gerencie os clientes atendidos pela sua operação."
                breadcrumb={[{ label: "Início", to: "/dashboard" }, { label: "Clientes" }]}
            />
            <DataTable
                dataTestId="customers"
                columns={columns}
                query={query.data}
                loading={query.isLoading}
                onSearch={(t) => {
                    setPage(1);
                    setSearch(t);
                }}
                onPageChange={setPage}
                onSortChange={setSort}
                onCreate={can("customers", "create") ? () => navigate("/customers/new") : null}
                createLabel="Novo cliente"
                onRowClick={(r) => can("customers", "update") && navigate(`/customers/${r.id}`)}
                emptyState={{ title: "Sem clientes", description: "Cadastre o primeiro cliente.", cta: "Novo cliente" }}
                initialSort={sort}
            />
            <ConfirmDialog
                open={!!confirm}
                onOpenChange={(open) => !open && setConfirm(null)}
                title="Excluir cliente?"
                description={`Esta ação é permanente. \"${confirm?.legal_name || ""}\" será removido.`}
                confirmLabel="Excluir"
                variant="destructive"
                onConfirm={() => {
                    if (confirm) del.mutate(confirm.id);
                    setConfirm(null);
                }}
            />
        </div>
    );
}
